import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import "./BookingConfirmation.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const SLOT_HOLD_NOTICE =
  "we confirm this time again when you confirm. If it has been taken, you'll be asked to pick another time.";
// Shown when a link WAS supplied but the token is expired / already stale. It no longer
// sends the customer away to find a newer email: the page below mints a fresh token in
// place once they confirm their booking details.
const RESCHEDULE_LINK_UNAVAILABLE_MESSAGE =
  "This reschedule link has expired, has already been used, or no longer matches the appointment.";

function formatDate(value) {
  if (!value) return "";
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function dateToYmd(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function ymdToLocalDate(value) {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function normalizeSlot(slot) {
  if (typeof slot === "string") {
    return { value: slot, label: slot };
  }

  const value = slot?.startTime || slot?.time24 || slot?.time || "";
  const label = slot?.time12 || slot?.label || value;
  return { value, label };
}

function formatCurrency(amount, currency = "usd") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
  }).format(Number(amount) || 0);
}

function BookingReschedulePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const paidLateRescheduleSessionId = searchParams.get("late_reschedule_session_id") || "";
  const paidLateRescheduleDate = searchParams.get("newDate") || "";
  const paidLateRescheduleStartTime = searchParams.get("newStartTime") || "";
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newDate, setNewDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [autoConfirmStarted, setAutoConfirmStarted] = useState(false);
  const [success, setSuccess] = useState(null);

  // Standalone fallback: shown when there is no token, or the token is rejected as
  // expired / stale / invalid. The customer supplies booking number + email + phone last
  // 4 and gets a token back in place - no email, no round trip.
  const [showLookup, setShowLookup] = useState(false);
  const [lookupForm, setLookupForm] = useState({
    bookingNumber: "",
    email: "",
    phoneLast4: "",
  });
  const [lookupSubmitting, setLookupSubmitting] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const booking = preview?.booking;
  const policy = preview?.policy;
  const durationForSlots = useMemo(() => {
    return Number(booking?.groupDurationMinutes) || Number(booking?.serviceDuration) || 0;
  }, [booking]);

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      if (!token) {
        // No link at all: offer the in-page lookup instead of a dead end.
        setShowLookup(true);
        setLoading(false);
        return;
      }

      setShowLookup(false);

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/bookings/reschedule/preview?token=${encodeURIComponent(token)}`,
        );
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok) {
          // A dead LINK is recoverable (mint a new one); a dead BOOKING is not.
          if (
            [
              "stale_token",
              "expired_token",
              "invalid_token",
              "invalid_signature",
              "invalid_payload",
              "invalid_purpose",
            ].includes(data.error)
          ) {
            setShowLookup(true);
            setError("");
            return;
          }
          throw new Error(data.message || "Unable to load this reschedule link.");
        }
        setPreview(data);
        setNewDate(data?.booking?.appointmentDate || "");
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load this reschedule link.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleLookupSubmit = async (event) => {
    event.preventDefault();
    setLookupError("");

    const bookingNumber = lookupForm.bookingNumber.trim();
    const email = lookupForm.email.trim();
    const phoneLast4 = lookupForm.phoneLast4.replace(/\D/g, "").slice(-4);

    if (!bookingNumber || !email || phoneLast4.length !== 4) {
      setLookupError(
        "Enter the booking number, the email used for the booking, and the last 4 digits of the phone.",
      );
      return;
    }

    setLookupSubmitting(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/bookings/reschedule/request-token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bookingNumber, email, phoneLast4 }),
        },
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data?.token) {
        throw new Error(
          data.message ||
            "We could not find a reschedulable appointment with those details.",
        );
      }

      // Putting the token in the URL re-runs the SAME preview/confirm flow the email
      // link uses, so the standalone path can never drift from it.
      setLoading(true);
      setLookupError("");
      setSearchParams({ token: data.token }, { replace: true });
    } catch (err) {
      setLookupError(err.message || "Unable to verify those details.");
    } finally {
      setLookupSubmitting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadSlots = async () => {      if (!booking?.serviceId || !booking?.staffId || !newDate || !durationForSlots || success) {
        setSlots([]);
        return;
      }

      setSlotsLoading(true);
      setSlotsError("");
      setSelectedSlot("");
      try {
        const params = new URLSearchParams({
          date: newDate,
          serviceId: booking.serviceId,
          staffId: booking.staffId,
          serviceDuration: String(durationForSlots),
        });
        const response = await fetch(`${API_BASE_URL}/api/bookings/available-slots?${params}`);
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok) {
          throw new Error(data.message || "Unable to load available times.");
        }
        setSlots(Array.isArray(data.availableSlots) ? data.availableSlots : []);
      } catch (err) {
        if (!cancelled) {
          setSlotsError(err.message || "Unable to load available times.");
          setSlots([]);
        }
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    };

    loadSlots();
    return () => {
      cancelled = true;
    };
  }, [booking, durationForSlots, newDate, success]);

  useEffect(() => {
    if (
      !booking ||
      success ||
      autoConfirmStarted ||
      !paidLateRescheduleSessionId ||
      !paidLateRescheduleDate ||
      !paidLateRescheduleStartTime
    ) {
      return;
    }

    setAutoConfirmStarted(true);
    setNewDate(paidLateRescheduleDate);
    setSelectedSlot(paidLateRescheduleStartTime);
    submitReschedule({
      targetDate: paidLateRescheduleDate,
      targetStartTime: paidLateRescheduleStartTime,
      lateRescheduleSessionId: paidLateRescheduleSessionId,
    });
  }, [
    autoConfirmStarted,
    booking,
    paidLateRescheduleDate,
    paidLateRescheduleSessionId,
    paidLateRescheduleStartTime,
    success,
  ]);

  async function startLateFeeCheckout({ targetDate, targetStartTime }) {
    const response = await fetch(`${API_BASE_URL}/api/bookings/reschedule/late-fee-checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, newDate: targetDate, newStartTime: targetStartTime }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || "Unable to start late reschedule fee payment.");
    }
    if (!data.stripeCheckoutUrl) {
      throw new Error("Late reschedule fee checkout did not return a payment link.");
    }
    window.location.href = data.stripeCheckoutUrl;
  }

  async function submitReschedule({ targetDate, targetStartTime, lateRescheduleSessionId = "" }) {
    setSubmitting(true);
    setError("");
    setSlotsError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/bookings/reschedule/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          newDate: targetDate,
          newStartTime: targetStartTime,
          lateRescheduleSessionId,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 402 && data.error === "reschedule_fee_required") {
        await startLateFeeCheckout({ targetDate, targetStartTime });
        return;
      }
      if (!response.ok) {
        throw new Error(data.message || "Unable to reschedule this appointment.");
      }
      setSuccess({
        booking: data.booking || { appointmentDate: targetDate, startTime: targetStartTime },
        paymentSummary: data.paymentSummary || {
          type: "free_reschedule",
          label: "Free reschedule",
          feeAmount: 0,
          currency: "usd",
        },
      });
    } catch (err) {
      setError(err.message || "Unable to reschedule this appointment.");
    } finally {
      setSubmitting(false);
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!newDate || !selectedSlot) {
      setSlotsError("Choose a new date and time before confirming.");
      return;
    }

    await submitReschedule({ targetDate: newDate, targetStartTime: selectedSlot });
  };

  return (
    <>
      <CustomerHeader />
      <main style={{ minHeight: "calc(100vh - 160px)", background: "#f8fafc", padding: "82px 18px 48px" }}>
        <section
          style={{
            maxWidth: 900,
            margin: "0 auto",
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
            overflow: "hidden",
          }}
        >
          {!success && (
            <div style={{ background: "#123c69", color: "white", padding: "26px 28px" }}>
              <h1 style={{ margin: 0, fontSize: 28, letterSpacing: 0 }}>Reschedule appointment</h1>
            </div>
          )}

          <div style={{ padding: 28 }}>
          {loading && <p style={{ margin: 0 }}>Loading appointment details...</p>}

          {!loading && showLookup && !booking && (
            <div>
              <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: 16, marginBottom: 18 }}>
                <strong>
                  {token
                    ? RESCHEDULE_LINK_UNAVAILABLE_MESSAGE
                    : "Reschedule your appointment"}
                </strong>
                <p style={{ margin: "6px 0 0 0" }}>
                  {token
                    ? "Enter your booking details below and you can reschedule straight away - no new link needed."
                    : "Enter your booking details to reschedule."}
                </p>
              </div>

              <form onSubmit={handleLookupSubmit}>
                <div style={{ display: "grid", gap: 12, maxWidth: 420 }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span style={{ fontWeight: 600 }}>Booking number</span>
                    <input
                      type="text"
                      value={lookupForm.bookingNumber}
                      onChange={(e) =>
                        setLookupForm((prev) => ({
                          ...prev,
                          bookingNumber: e.target.value.toUpperCase(),
                        }))
                      }
                      placeholder="BKG-0000000000000-000"
                      autoComplete="off"
                      style={{ padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }}
                      disabled={lookupSubmitting}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 4 }}>
                    <span style={{ fontWeight: 600 }}>Email used for the booking</span>
                    <input
                      type="email"
                      value={lookupForm.email}
                      onChange={(e) =>
                        setLookupForm((prev) => ({ ...prev, email: e.target.value }))
                      }
                      autoComplete="email"
                      style={{ padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }}
                      disabled={lookupSubmitting}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 4 }}>
                    <span style={{ fontWeight: 600 }}>Phone last 4 digits</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      value={lookupForm.phoneLast4}
                      onChange={(e) =>
                        setLookupForm((prev) => ({
                          ...prev,
                          phoneLast4: e.target.value.replace(/\D/g, "").slice(0, 4),
                        }))
                      }
                      style={{ padding: 10, border: "1px solid #d1d5db", borderRadius: 8, width: 120 }}
                      disabled={lookupSubmitting}
                    />
                  </label>
                </div>

                {lookupError && (
                  <p style={{ color: "#b42318", marginTop: 14 }}>{lookupError}</p>
                )}

                <button
                  type="submit"
                  disabled={lookupSubmitting}
                  style={{
                    marginTop: 16,
                    padding: "12px 20px",
                    background: lookupSubmitting ? "#9ca3af" : "#123c69",
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: lookupSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  {lookupSubmitting ? "Checking..." : "Find my appointment"}
                </button>
              </form>
            </div>
          )}

          {!loading && error && !booking && !showLookup && (
            <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 8, padding: 16 }}>
              <strong>We could not open this reschedule link.</strong>
              <p style={{ marginBottom: 0 }}>{error}</p>
            </div>
          )}

          {!loading && booking && !success && (
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gap: 12, marginBottom: 22 }}>
                <div><strong>{booking.serviceName}</strong></div>
                <div>{booking.shopName}</div>
                <div>{booking.staffName}</div>
                <div>
                  Current time: {formatDate(booking.appointmentDate)} at {booking.startTime}
                </div>
                {booking.groupBookingCount > 1 && (
                  <div>{booking.groupBookingCount} appointments will move together.</div>
                )}
              </div>

              {!policy?.canReschedule && (
                <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: 14, marginBottom: 18 }}>
                  This appointment is within {policy?.rescheduleWindowHours || 48} hours. Late reschedule fee payment will be available soon.
                </div>
              )}

              {policy?.canReschedule && (
                <>
                  {policy?.requiresLateFee && policy?.lateFee && (
                    <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: 14, marginBottom: 18 }}>
                      This appointment is within {policy.rescheduleWindowHours || 48} hours. A late reschedule fee of {formatCurrency(policy.lateFee.feeAmount, policy.lateFee.currency)} is required before the new time is confirmed.
                    </div>
                  )}
                  <div
                    style={{
                      border: "1px solid #fde68a",
                      background: "#fffbeb",
                      borderRadius: 8,
                      padding: 16,
                      marginBottom: 22,
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Select Date & Time</div>
                    {!selectedSlot && (
                      <p style={{ margin: "0 0 10px 0", color: "#92400e", fontSize: 12 }}>
                        Needs attention: select an available appointment time.
                      </p>
                    )}
                    {selectedSlot && (
                      <div
                        style={{
                          margin: "0 0 12px 0",
                          padding: "12px 14px",
                          borderRadius: 10,
                          border: "1px solid #fed7aa",
                          background: "#fff7ed",
                          color: "#9a3412",
                          fontSize: 13,
                          lineHeight: 1.5,
                        }}
                      >
                        <strong>Availability check:</strong> {SLOT_HOLD_NOTICE}
                      </div>
                    )}

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: 24,
                        alignItems: "start",
                      }}
                    >
                      <div>
                        <Calendar
                          onChange={(value) => {
                            const pickedDate = Array.isArray(value) ? value[0] : value;
                            const nextDate = dateToYmd(pickedDate);
                            if (!nextDate) return;
                            setNewDate(nextDate);
                            setSelectedSlot("");
                          }}
                          value={ymdToLocalDate(newDate)}
                          minDate={new Date()}
                        />
                        <div style={{ color: "#64748b", fontSize: 11, marginTop: 8 }}>
                          Grayed-out dates are unavailable for booking.
                        </div>
                      </div>

                      <div>
                        {slotsLoading && (
                          <div style={{ color: "#666", textAlign: "center", padding: 20, fontSize: 12 }}>
                            Loading available times...
                          </div>
                        )}
                        {slotsError && <p style={{ color: "#b42318", marginTop: 0 }}>{slotsError}</p>}
                        {!slotsLoading && !slotsError && slots.length === 0 && (
                          <div style={{ color: "#666", textAlign: "center", padding: 20, fontSize: 12 }}>
                            No available times for this date
                          </div>
                        )}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
                            gap: 8,
                            marginBottom: 22,
                          }}
                        >
                          {slots.map((slot, index) => {
                            const { value: slotTime, label: slotLabel } = normalizeSlot(slot);
                            if (!slotTime) return null;
                            const active = selectedSlot === slotTime;
                            return (
                              <button
                                key={`${slotTime}-${index}`}
                                type="button"
                                onClick={() => setSelectedSlot(slotTime)}
                                style={{
                                  padding: "8px 12px",
                                  borderRadius: 4,
                                  border: active ? "2px solid #2d6da3" : "1px solid #d1d5db",
                                  background: active ? "#f0f8ff" : "#ffffff",
                                  fontSize: 12,
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                }}
                              >
                                {slotLabel}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  {error && <p style={{ color: "#b42318" }}>{error}</p>}

                  <button
                    type="submit"
                    disabled={submitting || !selectedSlot}
                    style={{
                      background: submitting || !selectedSlot ? "#8b938d" : "#1f3d36",
                      color: "white",
                      border: 0,
                      borderRadius: 6,
                      padding: "12px 18px",
                      fontWeight: 700,
                      cursor: submitting || !selectedSlot ? "not-allowed" : "pointer",
                    }}
                  >
                    {submitting
                      ? paidLateRescheduleSessionId
                        ? "Finalizing..."
                        : "Confirming..."
                      : policy?.requiresLateFee
                        ? "Pay fee and confirm new time"
                        : "Confirm new time"}
                  </button>
                </>
              )}
            </form>
          )}

          {success && (() => {
            const confirmedBooking = success.booking || {};
            const paymentSummary = success.paymentSummary || {};
            const feeAmount = Number(paymentSummary.feeAmount || 0);
            const isPaidReschedule = paymentSummary.type === "late_reschedule_fee" && feeAmount > 0;

            return (
              <div className="confirmation-success" style={{ boxShadow: "none" }}>
                <div className="confirmation-success-header">
                  <div className="success-icon">OK</div>
                  <h1>Appointment Rescheduled</h1>
                  <p className="success-message">
                    Your appointment has been successfully updated.
                  </p>
                </div>

                <div className="booking-details">
                  <div className="detail-section">
                    <h2>Appointment Details</h2>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="label">Service</span>
                        <span className="value">{booking?.serviceName || "N/A"}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">New Time</span>
                        <span className="value">{formatDate(confirmedBooking.appointmentDate)} at {confirmedBooking.startTime}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section payment-summary">
                    <h2>Payment Summary</h2>
                    <div className="summary-grid">
                      <div className="summary-item">
                        <span>Reschedule Type</span>
                        <span>{isPaidReschedule ? "Late paid reschedule" : "Free reschedule"}</span>
                      </div>
                      {isPaidReschedule && paymentSummary.serviceSubtotal !== undefined && (
                        <div className="summary-item">
                          <span>Service Subtotal</span>
                          <span>{formatCurrency(paymentSummary.serviceSubtotal, paymentSummary.currency)}</span>
                        </div>
                      )}
                      {isPaidReschedule && paymentSummary.feePercent !== undefined && (
                        <div className="summary-item">
                          <span>Late Fee Rate</span>
                          <span>{Number(paymentSummary.feePercent || 0).toFixed(2)}%</span>
                        </div>
                      )}
                      <div className="summary-item total">
                        <span>{isPaidReschedule ? "Reschedule Fee Paid" : "Amount Due"}</span>
                        <span>{formatCurrency(feeAmount, paymentSummary.currency || "usd")}</span>
                      </div>
                    </div>
                  </div>

                  <div className="confirmation-note">
                    <p>A reschedule confirmation email has been sent to your email address.</p>
                  </div>
                </div>
              </div>
            );
          })()}
          </div>
        </section>
      </main>
      <CustomerFooter />
    </>
  );
}

export default BookingReschedulePage;
